import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { DocType, PermissionName } from "../../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { useUserData } from "../../../auth/useUserData.js";
import { FilesBoxTable } from "../../attachments/FilesBoxTable.js";
import { useItemAttachments } from "../../attachments/useAttachments.js";
import {
  ItemFilesBox_ItemFragment,
  useSetItemAttachmentClientVisbilityMutation,
} from "./ItemFilesBox.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  docType: DocType;
  viewerPermissions: PermissionName[];
  item: ItemFilesBox_ItemFragment;
  isEditable?: boolean;
  isInitiallyClosed?: boolean;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
  attachmentFilter?: (
    attachment: ItemFilesBox_ItemFragment["attachments"][number]
  ) => boolean;
}

export const ItemFilesBox = ({
  projectId,
  docId,
  docType,
  viewerPermissions,
  item,
  isEditable = true,
  isInitiallyClosed,
  onUpdateDataRefetchQueries,
  attachmentFilter,
}: Props) => {
  const { t } = useTranslate("Global");
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [setClientVisibility] = useSetItemAttachmentClientVisbilityMutation({
    client,
  });

  const setAttachmentClientVisibility = React.useCallback(
    async (attachmentId: string, clientVisibility: boolean) => {
      await setClientVisibility({
        variables: {
          input: {
            projectId,
            docId,
            itemId: item.id,
            attachmentId,
            clientVisibility,
          },
        },
      });
    },
    [docId, item.id, projectId, setClientVisibility]
  );

  const canChangeVisibility =
    item.authorOrganisationId === viewer.organisation.id;

  const { addAttachments, modifyAttachment, removeAttachment, loading } =
    useItemAttachments(item.id, onUpdateDataRefetchQueries);

  const filteredAttachments = React.useMemo(
    () =>
      attachmentFilter
        ? item.attachments.filter(attachmentFilter)
        : item.attachments,
    [item.attachments, attachmentFilter]
  );

  return (
    <FilesBoxTable
      title={t("Attachments")}
      attachments={filteredAttachments}
      isInitiallyClosed={isInitiallyClosed}
      renderImages={false}
      canEdit={isEditable}
      setClientVisibility={
        canChangeVisibility ? setAttachmentClientVisibility : undefined
      }
      addAttachments={addAttachments}
      modifyAttachment={modifyAttachment}
      removeAttachment={removeAttachment}
      loading={loading}
      renderEditIconButton={button => (
        <RestrictedByDocumentPermissionByTypeWithDebug
          permissionByType={{
            QUOTE: "MANAGE_QUOTES",
            REQUIREMENT: "MANAGE_REQUIREMENTS",
            TEMPLATE: "MANAGE_TEMPLATE",
            BUILDING: "MANAGE_ORG",
          }}
          document={{ docType, viewerPermissions }}
        >
          {button}
        </RestrictedByDocumentPermissionByTypeWithDebug>
      )}
    />
  );
};
